import React, { useState } from "react";
import {
  Badge,
  Body1,
  Button,
  Caption1,
  Card,
  Spinner,
  Text,
  Title3,
  makeStyles,
  mergeClasses,
  shorthands,
  tokens,
} from "@fluentui/react-components";
import {
  ArrowLeft24Regular,
  Checkmark24Regular,
  DocumentMultiple24Regular,
  FolderOpen24Regular,
  QuestionCircle24Regular,
  Send24Regular,
} from "@fluentui/react-icons";
import type { AccountInfo, IPublicClientApplication } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import { Page } from "../components/App";
import { useJob } from "../context/JobContext";
import { submitJobRequest } from "../../authConfig";
import { submitJob } from "../services/jobService";
import { mapBenSpecQuestionsForApi } from "../services/benSpecGuidance";
import type {
  BenSpecQuestionRow,
  BenefitSpecificationsJobRequest,
  SubmitJobRequest,
} from "../types";
import { taskPaneBackground } from "../theme";

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap(tokens.spacingVerticalM),
    boxSizing: "border-box",
    height: "88vh",
    ...taskPaneBackground,
    ...shorthands.padding(tokens.spacingVerticalL, tokens.spacingHorizontalL),
    overflow: "hidden",
  },
  heroCard: {
    flexShrink: 0,
    color: tokens.colorNeutralForeground1,
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border("1px", "solid", "#dbe4ef"),
    ...shorthands.borderRadius("14px"),
    ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalXL),
    boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap("2px"),
  },
  heroTitle: {
    margin: 0,
    color: tokens.colorNeutralForeground1,
  },
  heroBody: {
    color: tokens.colorNeutralForeground3,
    margin: 0,
  },
  heroEyebrow: {
    color: "#0f6cbd",
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase200,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    marginBottom: tokens.spacingVerticalXXS,
  },
  statsGrid: {
    flexShrink: 0,
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    ...shorthands.gap(tokens.spacingHorizontalS),
  },
  readinessCard: {
    flexShrink: 0,
    backgroundColor: "#eff6ff",
    ...shorthands.border("1px", "solid", "#bfdbfe"),
    ...shorthands.borderRadius("14px"),
    ...shorthands.padding(tokens.spacingVerticalM),
    boxShadow: "none",
  },
  readinessHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    ...shorthands.gap(tokens.spacingHorizontalS),
    marginBottom: tokens.spacingVerticalM,
  },
  readinessTitle: {
    display: "flex",
    alignItems: "center",
    ...shorthands.gap(tokens.spacingHorizontalS),
    color: "#123b63",
  },
  readyIcon: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "32px",
    height: "32px",
    color: "#ffffff",
    backgroundColor: "#0f6cbd",
    ...shorthands.borderRadius("9px"),
  },
  statCard: {
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap("2px"),
    backgroundColor: "rgba(255, 255, 255, 0.72)",
    ...shorthands.border("1px", "solid", "#dbeafe"),
    ...shorthands.borderRadius("10px"),
    ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalS),
    boxShadow: "none",
    minWidth: 0,
  },
  statValue: {
    color: "#0f3b66",
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
  },
  statLabel: {
    color: "#4b6480",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  contentScroll: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap(tokens.spacingVerticalM),
    paddingRight: tokens.spacingHorizontalXXS,
  },
  panelCard: {
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border("1px", "solid", "#dbe4ef"),
    ...shorthands.borderRadius("14px"),
    ...shorthands.padding(tokens.spacingVerticalM),
    boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
    flexShrink: 0,
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap(tokens.spacingVerticalM),
  },
  panelHeader: {
    display: "flex",
    alignItems: "center",
    ...shorthands.gap(tokens.spacingHorizontalS),
  },
  panelTitle: {
    margin: 0,
    fontWeight: tokens.fontWeightSemibold,
  },
  panelHeading: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
  },
  panelIcon: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "34px",
    height: "34px",
    flexShrink: 0,
    color: "#0f6cbd",
    backgroundColor: "#eff6ff",
    ...shorthands.border("1px", "solid", "#dbeafe"),
    ...shorthands.borderRadius("9px"),
  },
  panelCaption: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  panelBadge: {
    marginLeft: "auto",
  },
  sectionStack: {
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap(tokens.spacingVerticalS),
  },
  sectionCard: {
    backgroundColor: "#f8fafc",
    ...shorthands.border("1px", "solid", "#e2e8f0"),
    ...shorthands.borderRadius("10px"),
    ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalM),
    flexShrink: 0,
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap(tokens.spacingVerticalXS),
  },
  sectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    ...shorthands.gap(tokens.spacingHorizontalS),
  },
  summaryList: {
    listStyleType: "none",
    ...shorthands.margin(0),
    ...shorthands.padding(0),
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap(tokens.spacingVerticalXS),
  },
  summaryItem: {
    display: "flex",
    alignItems: "center",
    ...shorthands.gap(tokens.spacingHorizontalS),
    minWidth: 0,
    backgroundColor: "#f8fafc",
    ...shorthands.border("1px", "solid", "#e2e8f0"),
    ...shorthands.borderRadius("8px"),
    ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalM),
  },
  summaryName: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  folderCard: {
    backgroundColor: "#f8fafc",
    ...shorthands.border("1px", "solid", "#e2e8f0"),
    ...shorthands.borderRadius("8px"),
    ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalM),
    overflowWrap: "anywhere",
  },
  footerCard: {
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border("1px", "solid", "#dbe4ef"),
    ...shorthands.borderRadius("14px"),
    ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalXL),
    boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
    flexShrink: 0,
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap(tokens.spacingVerticalS),
  },
  guardWarning: {
    width: "100%",
    ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalM),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    ...shorthands.border("1px", "solid", "#c19c00"),
    backgroundColor: "#fff4ce",
    color: "#8a6d3b",
    boxSizing: "border-box",
  },
  submissionError: {
    width: "100%",
    ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalM),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    ...shorthands.border("1px", "solid", "#d13438"),
    backgroundColor: "#fde7e9",
    color: "#8a1c1f",
    boxSizing: "border-box",
  },
  footerActions: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    ...shorthands.gap(tokens.spacingHorizontalS),
  },
  footerButton: {
    minHeight: "44px",
    minWidth: 0,
  },
  footerMeta: {
    color: tokens.colorNeutralForeground3,
  },
});

async function getAccessTokenForSubmit(instance: IPublicClientApplication, account: AccountInfo) {
  try {
    const silent = await instance.acquireTokenSilent({
      ...submitJobRequest,
      account,
    });
    return silent.accessToken;
  } catch {
    const popupRequest: Parameters<IPublicClientApplication["acquireTokenPopup"]>[0] = {
      ...submitJobRequest,
      account,
    };
    const popup = await instance.acquireTokenPopup(popupRequest);
    return popup.accessToken;
  }
}

function groupQuestionsBySection(
  rows: BenSpecQuestionRow[]
): Array<{ section: string; rows: BenSpecQuestionRow[] }> {
  const groups = new Map<string, BenSpecQuestionRow[]>();
  rows.forEach((row) => {
    const section = row.section.trim() || "custom";
    const existing = groups.get(section) ?? [];
    existing.push(row);
    groups.set(section, existing);
  });
  return Array.from(groups.entries()).map(([section, groupedRows]) => ({
    section,
    rows: groupedRows,
  }));
}

interface BenSpecConfirmationPageProps {
  onNavigate: (page: Page) => void;
}

export const BenSpecConfirmationPage: React.FC<BenSpecConfirmationPageProps> = ({ onNavigate }) => {
  const classes = useStyles();
  const { state, dispatch } = useJob();
  const { instance, accounts } = useMsal();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);

  const {
    sourceFolderUrl,
    sourceFolderDriveId,
    sourceFolderItemId,
    selectedFiles,
    benSpecQuestions,
    benSpecQuestionSet,
    jobType,
  } = state;
  const groupedQuestions = React.useMemo(
    () => groupQuestionsBySection(benSpecQuestions),
    [benSpecQuestions]
  );
  const canSubmit =
    jobType === "BENEFIT_SPECIFICATION" &&
    selectedFiles.length > 0 &&
    benSpecQuestions.length > 0 &&
    Boolean(sourceFolderDriveId.trim() && sourceFolderItemId.trim());

  const handleSubmitJob = async () => {
    const account = accounts[0];
    if (!canSubmit || !account) {
      return;
    }
    setSubmissionError(null);
    setIsSubmitting(true);
    try {
      const accessToken = await getAccessTokenForSubmit(instance, account);
      const jobConfig: BenefitSpecificationsJobRequest = {
        source_folder_url: sourceFolderUrl,
        source_folder_drive_id: sourceFolderDriveId,
        source_folder_item_id: sourceFolderItemId,
        selected_files: selectedFiles,
        questions: mapBenSpecQuestionsForApi(benSpecQuestions),
        question_set_id: benSpecQuestionSet.id,
        question_set_name: benSpecQuestionSet.name,
        question_set_source: benSpecQuestionSet.source,
        question_set_version: benSpecQuestionSet.version,
        source_question_snapshot_hash: benSpecQuestionSet.snapshotHash,
      };
      const jobRequest: SubmitJobRequest = {
        job_type: "BENEFIT_SPECIFICATION",
        job_config: jobConfig,
      };
      const result = await submitJob(jobRequest, accessToken);
      dispatch({ type: "SET_CURRENT_BATCH_ID", payload: result.batch_id });
      onNavigate("dashboard");
    } catch (err) {
      console.error("Failed to submit ben spec job:", err);
      setSubmissionError(
        "We couldn't submit your job right now. Please try again in a moment. " +
          "If this keeps happening, refresh the add-in and sign in again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={classes.root}>
      <div className={classes.contentScroll}>
        <Card className={classes.heroCard}>
          <div className={classes.heroEyebrow}>Benefit specification · Final step</div>
          <Title3 className={classes.heroTitle}>Review and submit</Title3>
          <Body1 className={classes.heroBody}>
            Check the document scope and question set before starting the job.
          </Body1>
        </Card>

        <Card className={classes.readinessCard}>
          <div className={classes.readinessHeader}>
            <div className={classes.readinessTitle}>
              <span className={classes.readyIcon}>
                <Checkmark24Regular />
              </span>
              <Text size={400} weight="semibold">
                Specification ready
              </Text>
            </div>
            <Badge appearance="filled" color="brand" size="small">
              Ready
            </Badge>
          </div>
          <div className={classes.statsGrid}>
            <Card className={classes.statCard}>
              <Text className={classes.statValue}>{selectedFiles.length}</Text>
              <Caption1 className={classes.statLabel}>
                {selectedFiles.length === 1 ? "File" : "Files"}
              </Caption1>
            </Card>
            <Card className={classes.statCard}>
              <Text className={classes.statValue}>{benSpecQuestions.length}</Text>
              <Caption1 className={classes.statLabel}>Questions</Caption1>
            </Card>
            <Card className={classes.statCard}>
              <Text className={classes.statValue}>{groupedQuestions.length}</Text>
              <Caption1 className={classes.statLabel}>Sections</Caption1>
            </Card>
          </div>
        </Card>

        <Card className={classes.panelCard}>
          <div className={classes.panelHeader}>
            <span className={classes.panelIcon}>
              <FolderOpen24Regular />
            </span>
            <div className={classes.panelHeading}>
              <Text className={classes.panelTitle}>Source workspace</Text>
              <Text className={classes.panelCaption}>The folder containing your documents</Text>
            </div>
          </div>
          <div className={classes.folderCard}>
            <Text>{sourceFolderUrl || "Not specified"}</Text>
          </div>
        </Card>

        <Card className={classes.panelCard}>
          <div className={classes.panelHeader}>
            <span className={classes.panelIcon}>
              <DocumentMultiple24Regular />
            </span>
            <div className={classes.panelHeading}>
              <Text className={classes.panelTitle}>Documents</Text>
              <Text className={classes.panelCaption}>
                {selectedFiles.length} selected for processing
              </Text>
            </div>
            <Badge className={classes.panelBadge} appearance="tint" color="brand">
              {selectedFiles.length}
            </Badge>
          </div>
          <ul className={classes.summaryList}>
            {selectedFiles.map((file) => (
              <li key={file.url} className={classes.summaryItem} title={file.name}>
                <DocumentMultiple24Regular color="#0f6cbd" />
                <Text className={classes.summaryName}>{file.name}</Text>
              </li>
            ))}
          </ul>
        </Card>

        <Card className={classes.panelCard}>
          <div className={classes.panelHeader}>
            <span className={classes.panelIcon}>
              <QuestionCircle24Regular />
            </span>
            <div className={classes.panelHeading}>
              <Text className={classes.panelTitle}>Question set</Text>
              <Text className={classes.panelCaption}>Questions grouped by report section</Text>
            </div>
            <Badge className={classes.panelBadge} appearance="tint" color="brand">
              {benSpecQuestions.length}
            </Badge>
          </div>
          <Caption1 className={classes.statLabel}>
            {benSpecQuestionSet.name
              ? `Question source: ${benSpecQuestionSet.name}`
              : "Review the report labels that will be submitted."}
          </Caption1>
          <div className={classes.sectionStack}>
            {groupedQuestions.map((group) => (
              <div key={group.section} className={classes.sectionCard}>
                <div className={classes.sectionHeader}>
                  <Text weight="semibold">{group.section}</Text>
                  <Badge appearance="filled">{group.rows.length}</Badge>
                </div>
                <ul className={classes.summaryList}>
                  {group.rows.map((row) => (
                    <li key={row.id} className={classes.summaryItem}>
                      <QuestionCircle24Regular color="#0f6cbd" />
                      <Text className={classes.summaryName}>{row.questionLabel}</Text>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <div className={classes.footerCard}>
        {submissionError && (
          <Text role="alert" className={classes.submissionError}>
            {submissionError}
          </Text>
        )}
        {!canSubmit && (
          <Text size={300} className={classes.guardWarning}>
            Complete file selection, add at least one question, and ensure a folder URL is set
            before submitting.
          </Text>
        )}
        <div className={classes.footerActions}>
          <Button
            appearance="primary"
            icon={<ArrowLeft24Regular />}
            className={mergeClasses(classes.footerButton, "sourcestone-navigation-primary")}
            onClick={() => onNavigate("benSpecQuestions")}
            disabled={isSubmitting}
          >
            Back
          </Button>
          <Button
            appearance="primary"
            icon={isSubmitting ? <Spinner size="tiny" /> : <Send24Regular />}
            className={mergeClasses(classes.footerButton, "sourcestone-navigation-primary")}
            onClick={handleSubmitJob}
            disabled={isSubmitting || !canSubmit}
          >
            {isSubmitting ? "Submitting..." : "Submit job"}
          </Button>
        </div>
      </div>
    </div>
  );
};
